import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MARYLINE_HERO } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import {
  Heart, ShieldCheck, Sparkles, Clock, ArrowRight, Flag, Compass,
  Route as RouteIcon, MessageCircleHeart, Menu, X, Quote,
} from "lucide-react";

const NAV = [
  { label: "Accueil", href: "#top" },
  { label: "Comment ça marche ?", href: "#how" },
  { label: "À propos", href: "#why" },
];

const FEATURES = [
  {
    icon: MessageCircleHeart,
    title: "Ce que ses comportements montrent",
    desc: "Analysez ses actes, leur cohérence et les signaux qu'ils révèlent au-delà des mots.",
  },
  {
    icon: Flag,
    title: "Red & Green Flags",
    desc: "Identifiez en toute clarté les signaux rassurants et les points de vigilance réels.",
  },
  {
    icon: Compass,
    title: "Compatibilité",
    desc: "Évaluez la réciprocité, l'équilibre et la viabilité de votre dynamique, avec prudence.",
  },
  {
    icon: RouteIcon,
    title: "Plan d'action",
    desc: "Des étapes concrètes et mesurées pour avancer, poser une limite ou ouvrir la conversation.",
  },
];

const STEPS = [
  { n: "01", title: "Racontez votre situation", desc: "Une conversation naturelle avec Maryline, à votre rythme." },
  { n: "02", title: "Maryline vous écoute", desc: "Elle reformule et approfondit les éléments importants, sans jamais juger." },
  { n: "03", title: "Maryline prépare votre analyse", desc: "Après paiement, une analyse RawLove structurée de votre situation, appuyée sur les faits." },
  { n: "04", title: "Vous avancez", desc: "Un résultat personnalisé et un plan d'action pour retrouver votre clarté." },
];

const Badge = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-[#5C4A45]">
    <Icon className="w-4 h-4 text-[#6E2D38]" strokeWidth={1.75} />
    <span>{children}</span>
  </div>
);

export default function Home() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    trackEvent("landing_view");
  }, []);

  const start = () => {
    trackEvent("analysis_started_click");
    navigate("/chat?new=1");
  };

  return (
    <div id="top" className="min-h-screen bg-[#FAF7F2] text-[#2B1E1B] overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAF7F2]/80 border-b border-[#E8DDD5]/70">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <a href="#top" data-testid="logo-link" className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#6E2D38] fill-[#6E2D38]" />
            <span className="font-editorial text-2xl font-semibold tracking-tight">RawLove</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <a key={n.label} href={n.href} data-testid={`nav-${n.label}`}
                 className="text-sm text-[#5C4A45] hover:text-[#6E2D38] transition-colors">
                {n.label}
              </a>
            ))}
            <button onClick={start} data-testid="header-start-button"
              className="bg-[#6E2D38] hover:bg-[#54212A] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md">
              Commencer mon analyse
            </button>
          </nav>
          <button className="md:hidden p-2" data-testid="menu-toggle" onClick={() => setMenuOpen((v) => !v)}>
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-[#E8DDD5] bg-[#FFFDF9] px-5 py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <a key={n.label} href={n.href} onClick={() => setMenuOpen(false)}
                 className="text-[#5C4A45]">{n.label}</a>
            ))}
            <button onClick={start} data-testid="mobile-start-button"
              className="bg-[#6E2D38] text-white rounded-full px-5 py-3 text-sm font-medium">
              Commencer mon analyse
            </button>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 sm:pt-14 pb-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="relative order-1 animate-fade-up">
            <div className="absolute -inset-3 bg-[#F4E3E2] rounded-[2rem] rotate-[-2deg] -z-0" />
            <img src={MARYLINE_HERO} alt="Maryline, votre confidente RawLove"
              className="relative z-10 w-full h-[420px] sm:h-[540px] object-cover object-top rounded-[1.75rem] shadow-[0_20px_50px_-12px_rgba(110,45,56,0.25)]" />
            <div className="relative z-20 -mt-10 ml-4 inline-flex items-center gap-3 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-[0_10px_30px_-8px_rgba(110,45,56,0.2)] border border-[#E8DDD5]">
              <span className="w-2.5 h-2.5 rounded-full bg-[#2E6B4F] animate-pulse" />
              <div>
                <p className="text-sm font-semibold leading-tight">Maryline</p>
                <p className="text-xs text-[#8C6B64] leading-tight">Votre confidente · disponible</p>
              </div>
            </div>
          </div>

          <div className="order-2 animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <p className="text-xs font-sans tracking-widest uppercase text-[#8C6B64] font-semibold mb-4">
              Des réponses claires à vos questions amoureuses
            </p>
            <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1]">
              Comprenez.<br />Analysez.<br />
              <span className="text-[#6E2D38]">Avancez.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5C4A45] leading-relaxed max-w-lg">
              Parlez-en à Maryline. RawLove analyse les éléments de votre histoire pour comprendre
              ce que ses comportements montrent, mettre en lumière ce qui mérite votre attention et savoir comment avancer.
            </p>

            <div className="mt-7 inline-flex items-center gap-2 bg-[#F7EBEB] border border-[#C48B92]/30 rounded-full px-4 py-2">
              <Sparkles className="w-4 h-4 text-[#6E2D38]" />
              <span className="text-sm font-medium text-[#6E2D38]">Analyse complète • 4,99 €</span>
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button onClick={start} data-testid="hero-start-analysis-button"
                className="group inline-flex items-center justify-center gap-2 bg-[#6E2D38] hover:bg-[#54212A] text-white rounded-full px-7 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg">
                Analyser ma situation
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
              <Badge icon={ShieldCheck}>Confidentiel</Badge>
              <Badge icon={Sparkles}>Résultat personnalisé</Badge>
              <Badge icon={Clock}>Disponible rapidement</Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f, i) => (
            <div key={f.title} data-testid={`feature-card-${i}`}
              className="group bg-white rounded-2xl p-6 border border-[#E8DDD5] shadow-[0_4px_20px_-2px_rgba(110,45,56,0.06)] hover:shadow-[0_12px_30px_-4px_rgba(110,45,56,0.12)] hover:-translate-y-1 transition-all animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="w-11 h-11 rounded-xl bg-[#F4E3E2] flex items-center justify-center mb-4 group-hover:bg-[#6E2D38] transition-colors">
                <f.icon className="w-5 h-5 text-[#6E2D38] group-hover:text-white transition-colors" strokeWidth={1.75} />
              </div>
              <h3 className="font-editorial text-xl font-semibold mb-2 leading-snug">{f.title}</h3>
              <p className="text-sm text-[#5C4A45] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why RawLove */}
      <section id="why" className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="bg-[#FFFDF9] rounded-[2rem] border border-[#E8DDD5] p-8 sm:p-14 relative overflow-hidden">
          <Quote className="absolute -top-2 right-6 w-28 h-28 text-[#F4E3E2]" />
          <div className="relative max-w-3xl">
            <p className="text-xs font-sans tracking-widest uppercase text-[#8C6B64] font-semibold mb-4">Pourquoi RawLove</p>
            <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
              Parce que l'amour ne devrait pas être un jeu d'approximation.
            </h2>
            <p className="mt-6 text-base sm:text-lg text-[#5C4A45] leading-relaxed">
              RawLove vous aide à prendre du recul sur une situation émotionnellement difficile.
              Pas de promesse magique, pas de lecture de pensées : simplement un regard lucide sur
              ce que les comportements permettent réellement de dire.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                "Une analyse personnalisée, jamais générique",
                "Aucun jugement, un espace pour souffler",
                "Confidentialité prise au sérieux",
                "Des conseils concrets et réalistes",
                "La distinction claire entre faits et interprétations",
                "L'incertitude assumée plutôt qu'une fausse certitude",
              ].map((t) => (
                <div key={t} className="flex items-start gap-3">
                  <div className="mt-1 w-5 h-5 rounded-full bg-[#F4E3E2] flex items-center justify-center flex-shrink-0">
                    <Heart className="w-3 h-3 text-[#6E2D38] fill-[#6E2D38]" />
                  </div>
                  <span className="text-[#4A3B37]">{t}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-6xl mx-auto px-5 sm:px-8 pb-24">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-sans tracking-widest uppercase text-[#8C6B64] font-semibold mb-3">Le parcours</p>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">Comment ça marche</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.n} data-testid={`step-${i}`}
              className="bg-white rounded-2xl p-6 border border-[#E8DDD5] shadow-[0_4px_20px_-2px_rgba(110,45,56,0.06)] animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}>
              <span className="font-editorial text-4xl font-semibold text-[#C48B92]">{s.n}</span>
              <h3 className="mt-3 font-semibold text-lg leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm text-[#5C4A45] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <button onClick={start} data-testid="how-start-button"
            className="group inline-flex items-center justify-center gap-2 bg-[#6E2D38] hover:bg-[#54212A] text-white rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg">
            Analyser ma situation
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-4 text-sm text-[#8C6B64]">La conversation est gratuite. L'analyse complète est à 4,99 €.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#E8DDD5] bg-[#FFFDF9]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-[#6E2D38] fill-[#6E2D38]" />
            <span className="font-editorial text-xl font-semibold">RawLove</span>
          </div>
          <p className="text-sm text-[#8C6B64] text-center">
            RawLove n'est pas un service d'urgence ni un professionnel de santé mentale.
          </p>
          <div className="flex items-center gap-4 text-sm text-[#8C6B64]">
            <button onClick={() => navigate("/a-propos")} data-testid="footer-about-link"
              className="underline underline-offset-4 hover:text-[#6E2D38] transition-colors">À propos & transparence</button>
            <span>© {new Date().getFullYear()} RawLove</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
